import { IAITask } from '../../models/aiTaskModel';
import { instance, GETREQUESTOPTIONS } from '../apiService';

const TIMEOUT_MS = 1000 * 60 * 5; // 5 minutes
const getOptions = () => ({ ...GETREQUESTOPTIONS(), timeout: TIMEOUT_MS });


export const getAllTasks = async () => {
    const res = await instance.get<IAITask[]>('/api/ai-tasks', getOptions());
    return res.data;
};

export const getTaskByName = async (name: string) => {
    const res = await instance.get<IAITask>(`${'/api/ai-tasks'}/${name}`, getOptions());
    return res.data;
};

export const createTask = async (data: Partial<IAITask>) => {
    const res = await instance.post<IAITask>('/api/ai-tasks', data, getOptions());
    return res.data;
};

export const updateTask = async (name: string, data: Partial<IAITask>) => {
    const res = await instance.put<IAITask>(`${'/api/ai-tasks'}/${name}`, data, getOptions());
    return res.data;
};

export const deleteTask = async (name: string) => {
    const res = await instance.delete(`${'/api/ai-tasks'}/${name}`, getOptions());
    return res.data;
};

export const executeTask = async (name: string, input: Record<string, any>) => {
    const res = await instance.post(`${'/api/ai-tasks'}/execute/${name}`, { input }, getOptions());
    return res.data;
};

export const executeTaskStreaming = async function* (name: string, input: Record<string, any>) {
    const reqOptions = GETREQUESTOPTIONS();
    
    const response = await fetch(`/api/ai-tasks/execute/${name}/stream`, {
        method: 'POST',
        headers: {
            ...(reqOptions.headers || {}),
            'Accept': 'text/event-stream', 
            'Cache-Control': 'no-cache, no-transform' // Force browser to bypass cache/buffering layers
        },
        body: JSON.stringify({ input })
    });


    if (!response.ok || !response.body) throw new Error(`HTTP error! status: ${response.status}`);

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || ''; 

        for (const chunk of chunks) {
            const line = chunk.trim();
            if (line.startsWith('data: ')) {
                const data = line.slice(6).trim();
                if (data === '[DONE]') return;
                if (data) {
                    const parsedData = JSON.parse(data);
                    if (parsedData.error) {
                        throw new Error(parsedData.error);
                    }
                    yield parsedData;
                }
            }
        }
    }
};


export const testTaskConfig = async (
    config: {
        instructions: string;
        inputSchema?: Record<string, any>;
        outputSchema?: Record<string, any>;
        input?: Record<string, any>;
    }
) => {
    const res = await instance.post('/api/ai-tasks/test-config', config, getOptions());
    return res.data;
};

export const testTaskConfigStream = async function* (
    config: {
        instructions: string;
        inputSchema?: Record<string, any>;
        outputSchema?: Record<string, any>;
        input?: Record<string, any>;
    }
) {
    const reqOptions = GETREQUESTOPTIONS();
    
    const response = await fetch(`/api/ai-tasks/test-config/stream`, {
        method: 'POST',
        headers: {
            ...(reqOptions.headers || {}),
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream', 
            'Cache-Control': 'no-cache, no-transform'
        },
        body: JSON.stringify(config)
    });

    if (!response.ok || !response.body) throw new Error(`HTTP error! status: ${response.status}`);

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || ''; 

        for (const chunk of chunks) {
            const lines = chunk.split('\n');
            for (const line of lines) {
                if (line.trim().startsWith('data:')) {
                    const data = line.slice(line.indexOf('data:') + 5).trim();
                    if (data === '[DONE]') return;
                    if (data) {
                        try {
                            yield JSON.parse(data);
                        } catch (err) {
                            console.warn('Failed to parse SSE JSON:', data, err);
                        }
                    }
                }
            }
        }
    }
};